import { getBackendUrl } from "../lib/utils";
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as Lucide from 'lucide-react';
import axios from 'axios';

const renderIcon = (name, props = {}) => {
  const IconComponent = Lucide[name];
  return IconComponent ? <IconComponent {...props} /> : <Lucide.HelpCircle {...props} />;
};

const MissionVision = () => {
  const [data, setData] = useState({
    title: "Misyon & Vizyon",
    subtitle: "Geleceği İnşa Ediyoruz",
    intro: "Bölgemize ve ülkemize değer katacak yatırımlarımızla, sanayi ve ticaretin yeni standartlarını belirliyoruz.",
    vision: "İkitelli ve Silivri bölgesini, modern mimarisi, sürdürülebilir energy çözümleri ve güçlü altyapısı ile Türkiye'nin en inovatif üretim ve ticaret üssü haline getirmek. Küresel standartlarda iş alanları inşa ederek, üyelerimizin rekabet gücünü maksimize eden, çevreye duyarlı ve teknoloji entegrasyonu yüksek örnek bir kooperatif modeli yaratmak.",
    mission: "Üyelerimizin ihtiyaç ve beklentilerini en üst düzeyde karşılayan, fonksiyonel ve dayanıklı ticari yapılar inşa etmek. Proje süreçlerimizi şeffaflık, dürüstlük ve ortak akıl prensipleriyle yürüterek, yatırımların zamanında ve en yüksek kalite standartlarında tamamlanmasını sağlamak.",
    values: [
      {icon: "Zap", title: "Yenilikçilik", desc: "Modern mühendislik çözümleri ve akıllı bina teknolojileri ile sürekli gelişim odaklı projeler."},
      {icon: "Globe", title: "Sürdürülebilirlik", desc: "Doğaya saygılı, yeşil enerjiye uyumlu ve gelecek nesillere değer bırakan mimari anlayış."},
      {icon: "TrendingUp", title: "Değer Yaratma", desc: "Üyelerimizin yatırımlarını koruyan, bölgesel ekonomiye ivme kazandıran yüksek ticari getiri."}
    ]
  });

  useEffect(() => {
    const backendUrl = getBackendUrl();
    axios.get(`${backendUrl}/api/settings`)
      .then(res => {
        if (res.data.mission_vision) {
          setData(res.data.mission_vision);
        }
      })
      .catch(console.error);
  }, []);

  const valueColors = [
    "bg-gradient-to-br from-blue-600 to-indigo-700 shadow-blue-900/20 text-blue-200",
    "bg-gradient-to-br from-emerald-500 to-teal-700 shadow-emerald-900/20 text-emerald-200",
    "bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-900/20 text-amber-200"
  ];

  return (
    <div className="pt-32 pb-24 min-h-screen bg-slate-50 relative overflow-hidden">
      {/* Background colorful gradients */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-400/20 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-400/20 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 pointer-events-none"></div>

      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="inline-block py-1.5 px-4 rounded-full bg-blue-100 text-blue-700 font-bold text-sm mb-4">{data.subtitle}</span>
          <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 mb-6 tracking-tight">{data.title}</h1>
          <p className="text-xl text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">
            {data.intro}
          </p>
        </motion.div>

        <div className="space-y-16">
          {/* Vizyon Section */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-center gap-12 bg-white/80 backdrop-blur-xl p-10 md:p-14 rounded-[2.5rem] shadow-xl shadow-blue-900/5 border border-white"
          >
            <div className="w-24 h-24 shrink-0 bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-3xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              {renderIcon('Compass', { size: 48 })}
            </div>
            <div>
              <h2 className="text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Vizyonumuz</h2>
              <p className="text-lg text-slate-600 leading-relaxed font-medium">
                {data.vision}
              </p>
            </div>
          </motion.div>

          {/* Misyon Section */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row-reverse items-center gap-12 bg-white/80 backdrop-blur-xl p-10 md:p-14 rounded-[2.5rem] shadow-xl shadow-emerald-900/5 border border-white"
          >
            <div className="w-24 h-24 shrink-0 bg-gradient-to-br from-emerald-400 to-teal-600 text-white rounded-3xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
              {renderIcon('Target', { size: 48 })}
            </div>
            <div className="text-left md:text-right">
              <h2 className="text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Misyonumuz</h2>
              <p className="text-lg text-slate-600 leading-relaxed font-medium">
                {data.mission}
              </p>
            </div>
          </motion.div>

          {/* Core Values Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8"
          >
            {data.values && data.values.map((val, idx) => {
              const bgClass = valueColors[idx % valueColors.length];
              return (
                <div key={idx} className={`p-8 rounded-3xl text-white shadow-xl relative overflow-hidden group ${bgClass.split(' ')[0]} ${bgClass.split(' ')[1]}`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                  {renderIcon(val.icon, { size: 36, className: "mb-6 text-white/80" })}
                  <h3 className="text-2xl font-bold mb-3">{val.title}</h3>
                  <p className="text-white/90 font-medium">{val.desc}</p>
                </div>
              );
            })}
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default MissionVision;
